import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { creditos, fechaHora, gramos, mgAGramos, sufijoUnidad } from "../lib/formato";
import { Cargando } from "./piezas";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

/**
 * Ticket de la entrega, con el ancho de una impresora térmica.
 * Al imprimir, la hoja de estilos oculta todo lo demas y deja solo este bloque
 * (ver el bloque @media print de estilos.css).
 */

interface Linea {
  id: string;
  nombreProducto: string;
  precioCentimos: number;
  cantidadMg: number;
  subtotalCentimos: number;
  unidad: string;
  precioBaseCentimos: number;
  descuentoTexto: string | null;
}

interface Dispensacion {
  id: string;
  numero: number;
  fecha: string;
  totalCentimos: number;
  totalMg: number;
  descuentoCentimos: number;
  descuentoManualCentimos: number;
  descuentoManualTexto: string | null;
  metodoPago: string;
  estado: string;
  notas: string | null;
  socio: { numeroSocio: number; nombre: string; apellidos: string; saldoCentimos: number };
  usuario: { nombre: string } | null;
  lineas: Linea[];
}

interface Club {
  nombre: string;
  cif: string | null;
  direccion: string | null;
  ciudad: string | null;
  cp: string | null;
  telefono: string | null;
  ajustes: { textoTicket?: string; avisoLegal?: string; anchoTicketMm?: number };
}

export default function Ticket({ dispensacionId }: { dispensacionId: string }) {
  const { t } = useIdioma();
  const dispensacion = useQuery({
    queryKey: ["ticket", dispensacionId],
    queryFn: () => api<Dispensacion>(`/dispensaciones/${dispensacionId}`),
  });
  const club = useQuery({ queryKey: ["club"], queryFn: () => api<Club>("/club") });

  if (dispensacion.isLoading || club.isLoading || !dispensacion.data || !club.data) {
    return <Cargando texto={t("tk.preparando")} />;
  }

  const d = dispensacion.data;
  const c = club.data;
  // El papel de 80 mm imprime unos 72 mm útiles; el de 58 mm, unos 48 mm.
  const anchoUtil = (c.ajustes?.anchoTicketMm ?? 80) === 58 ? 48 : 72;

  return (
    <div className="ticket" style={{ ["--ancho-ticket" as string]: `${anchoUtil}mm` }}>
      <div className="ticket-centro">
        <strong className="ticket-nombre">{c.nombre.toUpperCase()}</strong>
        {c.cif && <div>CIF {c.cif}</div>}
        {c.direccion && <div>{c.direccion}</div>}
        {(c.cp || c.ciudad) && <div>{[c.cp, c.ciudad].filter(Boolean).join(" ")}</div>}
        {c.telefono && <div>Tel. {c.telefono}</div>}
      </div>

      <div className="ticket-linea" />

      <div className="ticket-fila">
        <span>{t("tk.ticket", { numero: d.numero })}</span>
        <span>{fechaHora(d.fecha)}</span>
      </div>
      <div className="ticket-fila">
        <span>{t("tk.socio", { numero: d.socio.numeroSocio })}</span>
        <span>
          {d.socio.nombre} {d.socio.apellidos}
        </span>
      </div>
      {d.usuario && (
        <div className="ticket-fila">
          <span>{t("tk.atiende")}</span>
          <span>{d.usuario.nombre}</span>
        </div>
      )}

      {d.estado === "ANULADA" && <div className="ticket-anulada">{t("tk.anulada")}</div>}

      <div className="ticket-linea" />

      {d.lineas.map((linea) => (
        <div key={linea.id} className="ticket-articulo">
          <div>{linea.nombreProducto}</div>
          <div className="ticket-fila">
            <span>
              {sufijoUnidad(linea.unidad) === "ud"
                ? t("tk.unidades", {
                    cantidad: Math.round(mgAGramos(linea.cantidadMg)),
                    precio: creditos(linea.precioCentimos),
                  })
                : t("tk.gramos", {
                    cantidad: mgAGramos(linea.cantidadMg).toLocaleString("es-ES", { minimumFractionDigits: 2 }),
                    precio: creditos(linea.precioCentimos),
                  })}
            </span>
            <span>{creditos(linea.subtotalCentimos)}</span>
          </div>
          {linea.descuentoTexto && (
            <div className="ticket-descuento">
              {t("tk.catalogo", {
                texto: linea.descuentoTexto,
                importe: creditos(linea.precioBaseCentimos),
              })}
            </div>
          )}
        </div>
      ))}

      <div className="ticket-linea" />

      <div className="ticket-fila ticket-total">
        <span>{t("tk.total")}</span>
        <span>{creditos(d.totalCentimos)}</span>
      </div>
      {d.descuentoCentimos > 0 && (
        <div className="ticket-fila">
          <span>{t("tk.descuentoAplicado")}</span>
          <span>-{creditos(d.descuentoCentimos)}</span>
        </div>
      )}
      {d.descuentoManualCentimos > 0 && (
        <div className="ticket-fila">
          {/* Sin esto el total del papel no cuadra con la suma de las lineas. */}
          <span>
            {d.descuentoManualTexto
              ? t("tk.descuentoCon", { texto: d.descuentoManualTexto })
              : t("tk.descuento")}
          </span>
          <span>-{creditos(d.descuentoManualCentimos)}</span>
        </div>
      )}
      <div className="ticket-fila">
        <span>{t("tk.totalDispensado")}</span>
        <span>{gramos(d.totalMg)}</span>
      </div>
      <div className="ticket-fila">
        <span>{t("tk.formaDePago")}</span>
        <span>{t(`pago.${d.metodoPago}` as Clave)}</span>
      </div>
      {d.metodoPago === "CARTERA" && (
        <div className="ticket-fila">
          <span>{t("tk.saldoRestante")}</span>
          <span>{creditos(d.socio.saldoCentimos)}</span>
        </div>
      )}

      {d.notas && (
        <>
          <div className="ticket-linea" />
          <div>{d.notas}</div>
        </>
      )}

      <div className="ticket-linea" />

      <div className="ticket-centro ticket-pie">
        {c.ajustes?.textoTicket && <p>{c.ajustes.textoTicket}</p>}
        {c.ajustes?.avisoLegal && <p>{c.ajustes.avisoLegal}</p>}
        <p>{t("tk.documentoInterno")}</p>
      </div>
    </div>
  );
}
